import React, { createContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User, authApi } from '../services/api/auth.api';
import { setAuthToken } from '../services/api/client';
import { alVencerSesion } from '../services/sesion-events';
import { olvidarSesionActiva } from '../modules/campana-nueva/api';

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (tokens: { accessToken: string }, user: User) => void;
    logout: () => void;
    checkAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Sesión persistente en localStorage.
 * Claves:
 *  - DEENEX_AUTH_TOKEN: accessToken
 *  - DEENEX_AUTH_USER:  user serializado
 *
 * En modo demo (mock mode) NO hay refresh por cookie HTTP-only — la sesión
 * se restaura desde localStorage en el boot, así un refresh no desloguea.
 * En producción la cookie refresh sigue funcionando como segundo intento.
 */
const TOKEN_KEY = 'DEENEX_AUTH_TOKEN';
const USER_KEY = 'DEENEX_AUTH_USER';

/**
 * ACCESO DIRECTO (sin pantalla de login).
 *
 * Con `VITE_ACCESO_DIRECTO=true`, si no hay sesión el front pide un token al backend y entra
 * solo. Es para el deploy de DEENEX Activa operado por nosotros (pilotos done-for-you), donde
 * frenar en un login que todavía no manda mails no protege nada y sólo traba la demo.
 *
 * ⚠️ Con esto, CUALQUIERA que tenga la URL entra. No es un descuido: el backend es fail-closed
 * y `dev-login` fuera de development acuña SIEMPRE para `DEMO_BRAND_ID` ignorando lo que se pida
 * (routes/index.ts), así que no hay forma de saltar a otra marca — el alcance es la marca demo.
 * Cuando haya login real (OTP), esta variable se apaga y no hay que tocar código.
 */
const ACCESO_DIRECTO = (import.meta as any).env?.VITE_ACCESO_DIRECTO === 'true';
const ACTIVA_API = (import.meta as any).env?.VITE_ACTIVA_API || 'http://localhost:3005/api/v1';
const DEMO_BRAND = (import.meta as any).env?.VITE_DEMO_BRAND || 'hatsu-demo';

/** Pide un token al backend y arma la sesión. null = no se pudo (se cae al login normal). */
async function sesionDirecta(): Promise<{ token: string; user: User } | null> {
    try {
        const r = await fetch(`${ACTIVA_API}/auth/dev-login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ brandId: DEMO_BRAND }),
        });
        const j = await r.json();
        const token = j?.data?.token;
        if (!token) return null;
        // el brandId REAL es el que devuelve el backend, no el que pedimos (puede forzarlo)
        const brandId = j?.data?.brandId || DEMO_BRAND;
        return {
            token,
            user: { id: brandId, email: `${brandId}@deenex.local`, role: 'BRAND_OWNER', name: 'DEENEX Activa' } as User,
        };
    } catch {
        return null;
    }
}

function readStoredSession(): { token: string; user: User } | null {
    try {
        const token = localStorage.getItem(TOKEN_KEY);
        const userRaw = localStorage.getItem(USER_KEY);
        if (!token || !userRaw) return null;
        const user = JSON.parse(userRaw) as User;
        return { token, user };
    } catch {
        return null;
    }
}

function writeStoredSession(token: string, user: User) {
    try {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {
        /* ignore quota errors */
    }
}

function clearStoredSession() {
    try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    } catch {
        /* ignore */
    }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    // El usuario actual para el aviso de sesión vencida, que se suscribe una sola vez.
    const userRef = useRef<User | null>(null);
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    // El comercio y el token de Activa son de la sesión: al entrar o salir se olvidan, así quien entra
    // después en el mismo navegador no sigue con el token de Activa (y el mail) del anterior.
    const login = (tokens: { accessToken: string }, userData: User) => {
        olvidarSesionActiva();
        setAuthToken(tokens.accessToken);
        setUser(userData);
        writeStoredSession(tokens.accessToken, userData);
        setIsLoading(false);
    };

    const logout = async () => {
        try {
            await authApi.logout();
        } catch (error) {
            console.error('Logout error', error);
        } finally {
            olvidarSesionActiva();
            setAuthToken(null);
            setUser(null);
            clearStoredSession();
        }
    };

    /**
     * Sesión vencida (services/sesion-events): el refresh falló, o el canje de Activa dijo que la
     * persona ya no tiene acceso. Se cierra la sesión acá; con `user` en null, ProtectedRoute lleva a
     * /login y guarda adónde se quería ir. Sin usuario no hay nada que cerrar (el login mismo, las
     * páginas públicas).
     */
    useEffect(() => alVencerSesion(() => {
        if (!userRef.current) return;
        userRef.current = null;
        olvidarSesionActiva();
        setAuthToken(null);
        clearStoredSession();
        setUser(null);
    }), []);

    /**
     * Restore session on boot:
     *  1. Try localStorage (works in mock mode AND production)
     *  2. If no localStorage, try refresh cookie (production only)
     *  3. If both fail, user goes to login
     */
    const checkAuth = async () => {
        setIsLoading(true);

        // 1. Try localStorage first
        const stored = readStoredSession();
        if (stored) {
            setAuthToken(stored.token);
            setUser(stored.user);
            setIsLoading(false);
            return;
        }

        // 2. Fallback: try refresh cookie (real backend only)
        try {
            const response = await authApi.refreshToken();
            // Sin user la respuesta no sirve: antes se fabricaba uno ('restored', user@example.com)
            // y se entraba con una identidad inventada. El backend siempre lo devuelve.
            if (response.success && response.data?.user) {
                setAuthToken(response.data.accessToken);
                setUser(response.data.user);
                writeStoredSession(response.data.accessToken, response.data.user);
            } else {
                setAuthToken(null);
                setUser(null);
            }
        } catch (error) {
            // Esperado en mock mode (el handler devuelve 401)
            setAuthToken(null);
            setUser(null);
        } finally {
            // 3. Último recurso: acceso directo (ver ACCESO_DIRECTO). Sin esto el operador queda
            //    trabado en un login que todavía no puede completar (el OTP necesita SMTP).
            if (ACCESO_DIRECTO && !readStoredSession()) {
                const s = await sesionDirecta();
                if (s) {
                    setAuthToken(s.token);
                    setUser(s.user);
                    writeStoredSession(s.token, s.user);
                }
            }
            setIsLoading(false);
        }
    };

    useEffect(() => {
        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            isLoading,
            login,
            logout,
            checkAuth
        }}>
            {children}
        </AuthContext.Provider>
    );
};
